import { Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import {
  AdoptionPads,
  useAdoptionDraft,
  type Adoption,
} from '@/signing/AdoptSignature'

interface ConsentAndAdoptProps {
  signerName: string
  envelopeTitle: string
  consentText: string
  needsInitials: boolean
  agreeing: boolean
  declining: boolean
  error: string | null
  onAgree: (adoption: Adoption) => void
  onDecline: () => void
}

/**
 * Consent and signature adoption in one step, for a signer the sender delivers
 * (already signed in to the sender's app). Consent still comes before the
 * document. It skips the name and email confirmation of the adopt dialog,
 * because the sender has already established who the signer is.
 */
export function ConsentAndAdopt({
  signerName,
  envelopeTitle,
  consentText,
  needsInitials,
  agreeing,
  declining,
  error,
  onAgree,
  onDecline,
}: ConsentAndAdoptProps) {
  const draft = useAdoptionDraft(signerName, needsInitials)
  const { adoption } = draft

  return (
    <Card className="mx-auto max-w-xl">
      <CardHeader>
        <CardTitle>Consent and adopt your signature</CardTitle>
        <CardDescription>
          Hi {signerName}. Before you can view {envelopeTitle}, please agree to sign
          electronically and create your signature. You will click each box on the
          document to apply it.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="max-h-64 overflow-y-auto rounded-md border border-border bg-muted/40 p-4 text-sm leading-6 text-foreground whitespace-pre-wrap">
          {consentText}
        </div>
        <AdoptionPads draft={draft} />
        {error ? <p className="text-sm text-destructive">{error}</p> : null}
        <div className="flex flex-wrap gap-2">
          <Button
            disabled={!adoption || agreeing}
            onClick={() => {
              if (adoption) {
                onAgree(adoption)
              }
            }}
          >
            {agreeing ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            I agree and adopt this signature
          </Button>
          <Button type="button" variant="outline" disabled={declining} onClick={onDecline}>
            I don’t agree
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}
